import LocalStoreManager from "./local-store-manager";
import GetCompanyProxy from "../proxies/back-end/company/get";
import GetChargedAmountProxy from "../proxies/back-end/company/charge-amount";
import GetSubscriptionPriceProxy from "../proxies/back-end/subscriptions/get-subscription-price";
import PaymentServiceProxy from "../proxies/back-end/subscriptions/payment.microservice";
import axios from "axios";

// Utilities
import MessagingTopic from "./messaging-topic";
import { forEach, uniq, uniqBy } from "lodash";
import GetPriceProxy from "../proxies/back-end/subscriptions/get-price";
import SubscriptionServiceProxy from "@/proxies/back-end/subscriptions/subscription.microservice";
import { getAppFeatureProp } from "@/utilities/app-feature-util";
import getPlatformConfig from "@/utilities/get-platform-config";
import getAppCustomEnv from "@/utilities/get-app-custom-env";

import { Constants } from "customer-sdk/constants";

const COMPANY_STATUSES = Constants.COMPANY_STATUSES;
const PARTNER_COMPANY_STATUSES = Constants.PARTNER_COMPANY_STATUSES;
const SHOPPING_CART_ITEMS = Constants.SHOPPING_CART_ITEMS;

// Shared Variables
let globalTopic = new MessagingTopic();

//#region Private Methods
let getToken = () => {
  let store = new LocalStoreManager();

  return store.getToken();
};
//#endregion

/**
 * @class ShoppingCartManager
 * @description Helper used for managing shopping cart.
 * @author Koh Keng Hong
 * @version 1.0
 * @since December 13, 2018
 */
export default class ShoppingCartManager {
  /**
   * @function fetchShoppingCartDetails
   * @description Method used for fetching shopping cart details.
   */
  // eslint-disable-next-line complexity
  async fetchShoppingCartDetails(
    subscriptionIds = null,
    oneOffCodes = null,
    couponEnabled = false,
    companyIdFromBillingProfile = null,
    previewDiscount = false,
    appFeatures = null,
    couponCode = null
  ) {
    let store = new LocalStoreManager();
    let companyId = await store.getCompanyId();

    if ((companyId == null || companyId === "") && companyIdFromBillingProfile === null) {
      return;
    } else if (companyIdFromBillingProfile) {
      companyId = companyIdFromBillingProfile;
    }

    let authToken = getToken();
    let company = await this.fetchCompany(companyId, authToken);

    if (company == null) {
      return;
    }

    const appCustomEnv = await getAppCustomEnv();
    if (appCustomEnv.VUE_APP_SLEEK_BILLING_API) {
      try {
        const response = await axios.get(`${appCustomEnv.VUE_APP_SLEEK_BILLING_API}/api/config`);
        if (response.data.SWITCH_TO_SLEEK_BILLINGS === "enabled") {
          return;
        }
      } catch (error) {
        console.error("Failed to fetch billing config:", error);
      }
    }

    if (company.microservice_enabled) {
      return this.handleMicroserviceShoppingCartDetails(
        company,
        companyId,
        authToken,
        couponEnabled,
        subscriptionIds || [],
        previewDiscount,
        appFeatures,
        couponCode
      );
    } else if (
      company.status == COMPANY_STATUSES.DRAFT.value ||
      company.status == PARTNER_COMPANY_STATUSES.PARTNER_DRAFT.value
    ) {
      return this.fetchOnboardingShoppingCartDetails(companyId, authToken, couponEnabled);
    } else {
      if (subscriptionIds && oneOffCodes) {
        return this.fetchShoppingCartSubscriptionAndCodesDetails(
          companyId,
          authToken,
          subscriptionIds,
          oneOffCodes,
          couponEnabled
        );
      } else if (subscriptionIds && subscriptionIds.length > 0 && !oneOffCodes) {
        return this.fetchShoppingCartSubscriptionsDetails(companyId, authToken, subscriptionIds, couponEnabled);
      }
      return this.setShoppingCartForNoSubscriptions();
    }
  }

  /**
   * @function setShoppingCartForNoSubscriptions
   * @description Method for setting shopping cart details when there are no subscriptions and onboarding is completed.
   */
  setShoppingCartForNoSubscriptions() {
    return {
      shoppingCart: [],
      shoppingCartTotal: 0,
      isCouponApplied: false,
    };
  }

  /**
   * @function fetchCompany
   * @description Method for fetching company details.
   * @param {String} id Company Id.
   * @param {String} authToken Authentication Token.
   */
  async fetchCompany(id, authToken) {
    let proxy = new GetCompanyProxy();
    return await proxy.execute({ id, authToken });
  }

  /**
   * @function fetchOnboardingShoppingCartDetails
   * @description Method for fetching shopping cart details for onboarding.
   * @param {String} id Company Id.
   * @param {String} authToken Authentication Token.
   */
  async fetchOnboardingShoppingCartDetails(id, authToken, couponEnabled) {
    let proxy = new GetChargedAmountProxy();
    let chargedAmount = await proxy.execute({ id, authToken, couponEnabled });
    let shoppingCart = [];
    let isCouponApplied = false;
    if (chargedAmount.items) {
      let fetchedShoppingCart = chargedAmount.items.reduce((map, obj) => {
        map[obj.code] = {
          description: obj.description,
          amount: obj.amount,
          code: obj.code,
          couponCode: obj.couponCode,
          isIncludedOnAccountingPlan: obj.isIncludedOnAccountingPlan,
        };
        return map;
      }, {});
      isCouponApplied = fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.COUPON_DISCOUNT);
      shoppingCart = await this.translateShoppingCartItems(fetchedShoppingCart);
    }
    return {
      shoppingCart,
      shoppingCartSubTotal: chargedAmount.subTotal ? chargedAmount.subTotal : 0,
      shoppingCartDiscount: chargedAmount.totalDiscount ? chargedAmount.totalDiscount : 0,
      shoppingCartTotal: chargedAmount.totalAmount,
      isCouponApplied,
    };
  }

  // eslint-disable-next-line max-lines-per-function
  // use for SG Alpha Onboarding flow microservice
  async fetchOnboardingShoppingCartDetailsMicroservice(id, authToken, previewDiscount, couponCode) {
    const companyProxy = new GetCompanyProxy();
    let subscriptionProxy = new SubscriptionServiceProxy();
    const paymentProxy = new PaymentServiceProxy();

    const store = new LocalStoreManager();
    const isPartner = store.getPartnerId() !== "null";

    let selectedServices = await companyProxy.getCompanySelectedServices({ id, authToken });

    if (isPartner) {
      selectedServices = selectedServices.filter(
        (selectedService) =>
          !["xero_subscription_(1_year)", "xero_multi_currency_upgrade"].includes(selectedService.service)
      );
    }

    const getServicePayload = {
      authToken,
      internalNames: selectedServices.map((item) => item.service),
    };
    const allServices = await subscriptionProxy.getServicesList(getServicePayload);

    const actionItems = [];

    selectedServices.forEach((item) => {
      const selectedItem = allServices.find((serviceItem) => item.service == serviceItem.internalName);
      if (selectedItem) {
        const preparedActionItem = {
          itemId: selectedItem._id,
          name: selectedItem.name,
          price: item.included ? 0 : selectedItem.price,
          code: selectedItem.code,
          quantity: 1,
          type: selectedItem.type,
          included: item.included,
        };
        actionItems.push(preparedActionItem);
      }
    });

    const previewCheckout = await paymentProxy.getCheckoutItems(
      { authToken },
      { companyId: id, items: actionItems, previewDiscount, couponCode }
    );

    if (previewCheckout == null) {
      return;
    }

    const { checkoutItems, subTotalAmount, totalAmount, totalDiscount, error } = previewCheckout;

    const shoppingCart = checkoutItems.map((item) => ({
      description: item.name,
      amount: item.price,
      code: item.code,
      type: item.type,
      couponCode: item.type === SHOPPING_CART_ITEMS.COUPON_DISCOUNT ? item.description : null,
      included: item.included,
    }));

    return {
      shoppingCart,
      shoppingCartSubTotal: subTotalAmount,
      shoppingCartDiscount: totalDiscount,
      shoppingCartTotal: totalAmount,
      isCouponApplied: totalDiscount != 0,
      error,
    };
  }

  /**
   * @function fetchShoppingCartSubscriptionsDetails
   * @description Method for fetching shopping cart details for subscriptions.
   * @param {String} id Company Id.
   * @param {String} authToken Authentication Token.
   * @param {Array} companySubscriptionIds Subscription Ids.
   */
  async fetchShoppingCartSubscriptionsDetails(id, authToken, companySubscriptionIds, couponEnabled) {
    let proxy = new GetSubscriptionPriceProxy();
    let chargedAmount = await proxy.execute({
      id,
      authToken,
      companySubscriptionIds,
      couponEnabled,
    });
    let shoppingCart = [];
    let isCouponApplied = false;
    if (chargedAmount.items) {
      let fetchedShoppingCart = chargedAmount.items.reduce((map, obj) => {
        map[obj.code] = {
          description: obj.description,
          amount: obj.amount,
          code: obj.code,
          couponCode: obj.couponCode,
        };
        return map;
      }, {});
      isCouponApplied = fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.COUPON_DISCOUNT);
      shoppingCart = await this.translateShoppingCartItems(fetchedShoppingCart);
    }
    return {
      shoppingCart,
      shoppingCartSubTotal: chargedAmount.subTotal ? chargedAmount.subTotal : 0,
      shoppingCartDiscount: chargedAmount.totalDiscount ? chargedAmount.totalDiscount : 0,
      shoppingCartTotal: chargedAmount.totalAmount,
      isCouponApplied,
    };
  }

  async fetchShoppingCartSubscriptionsDetailsMicroservice(
    authToken,
    subscriptionIds,
    companyId,
    appFeatures,
    couponCode = null
  ) {
    if (!appFeatures) {
      const platformConfig = await getPlatformConfig(window.location.hostname);
      appFeatures = platformConfig.cmsGeneralFeatureList;
    }

    const subscriptionServiceProxy = new SubscriptionServiceProxy();
    const paymentProxy = new PaymentServiceProxy();

    if (subscriptionIds.length === 0) {
      return;
    }

    const subscriptions = await subscriptionServiceProxy.getSubscriptionsByIds({
      authToken,
      companyIds: [companyId],
      statuses: ["active", "overdue"],
    });

    const renewalSubscriptions = subscriptions.filter((subscription) => subscriptionIds.includes(subscription._id));

    let includedServiceIds = [];
    for (let subscription of renewalSubscriptions) {
      if (!subscription.service) {
        continue;
      }

      if (subscription.service.includedServiceIds && subscription.service.includedServiceIds.length > 0) {
        includedServiceIds = [...includedServiceIds, ...subscription.service.includedServiceIds];
      }

      const isIncluded = !!subscriptions.find(
        (sub) => sub.service.includedServiceIds && sub.service.includedServiceIds.includes(subscription.service._id)
      );

      if (isIncluded) {
        includedServiceIds.push(subscription.service._id);
      }
    }
    includedServiceIds = uniq(includedServiceIds);

    const includedSubscriptions = subscriptions.filter((subscription) =>
      includedServiceIds.includes(subscription.service._id)
    );

    const billingServiceAppFeature = getAppFeatureProp(appFeatures, "billing_service");
    const shiftCode = getAppFeatureProp(billingServiceAppFeature.props, "shift_code");
    const shiftCodeValue = shiftCode.value ?? {};

    let allRenewalSubscriptions = [...renewalSubscriptions, ...includedSubscriptions];
    allRenewalSubscriptions = uniqBy(allRenewalSubscriptions, (d) => d._id);

    const newShiftedServices = [];

    const actionItems = [];
    for (let subscription of allRenewalSubscriptions) {
      let { service } = subscription;
      if (!subscription.service) {
        continue;
      }

      const newShiftCode = shiftCodeValue[service.code];
      const isIncludedService = includedServiceIds.includes(service._id);

      if (newShiftCode) {
        const getServicePayload = {
          authToken,
          codes: [newShiftCode],
        };
        const shiftedService = await subscriptionServiceProxy.getServicesList(getServicePayload);

        if (shiftedService && shiftedService.length) {
          service = shiftedService[0];
          newShiftedServices.push(service);
        }
      }

      actionItems.push({
        itemId: service._id,
        name: service.name,
        price: isIncludedService ? 0 : subscription.customPrice ?? service.price,
        code: service.code,
        quantity: 1,
        type: service.type,
      });
    }

    if (newShiftedServices.length > 0) {
      newShiftedServices.forEach(service => {
        if (service.includedServiceIds && service.includedServiceIds.length > 0) {
          includedServiceIds = [...includedServiceIds, ...service.includedServiceIds];
        }
      })
      includedServiceIds = uniq(includedServiceIds);
    }

    actionItems.forEach(actionItem => {
      if (includedServiceIds.includes(actionItem.itemId)) {
        actionItem.price = 0;
      }
    })

    // Add new ADD-ONs services to preview checkout
    const newIncludedServiceIds = includedServiceIds.filter((id) => !actionItems.find((actionItem) => actionItem.itemId === id));

    if (newIncludedServiceIds.length) {
      const getServicePayload = {
        authToken,
        ids: newIncludedServiceIds,
      };
      const newAddOnServices = await subscriptionServiceProxy.getServicesList(getServicePayload);

      if (newAddOnServices.length) {
        for (let newAddOnService of newAddOnServices) {
          actionItems.push({
            itemId: newAddOnService._id,
            name: newAddOnService.name,
            price: 0,
            code: newAddOnService.code,
            quantity: 1,
            type: newAddOnService.type,
          });
        }
      }
    }

    const previewCheckout = await paymentProxy.getCheckoutItems(
      { authToken },
      { companyId, items: actionItems, couponCode }
    );

    if (previewCheckout == null) {
      return;
    }

    const { checkoutItems, subTotalAmount, totalDiscount, totalAmount, totalTax } = previewCheckout;

    const shoppingCart = checkoutItems.map((item) => ({
      description: item.name,
      amount: item.price,
      code: item.code,
    }));

    return {
      shoppingCart,
      shoppingCartSubTotal: subTotalAmount,
      shoppingCartDiscount: totalDiscount,
      shoppingCartTotal: totalAmount,
      shoppingCartTotalTax: totalTax,
      isCouponApplied: false,
      checkoutItems,
    };
  }

  // function to fetch checkout items for payment request
  // separate function is required as payment request can have custom price
  async fetchShoppingCartDetailsForPaymentRequest(items) {
    let authToken = getToken();
    let store = new LocalStoreManager();
    let companyId = await store.getCompanyId();

    const paymentProxy = new PaymentServiceProxy();
    const previewCheckout = await paymentProxy.getCheckoutItems(
      { authToken },
      { companyId, items }
    );

    if (previewCheckout == null) {
      return;
    }

    const { subTotalAmount, totalDiscount, totalAmount, totalTax } = previewCheckout;

    return {
      shoppingCartSubTotal: subTotalAmount,
      shoppingCartDiscount: totalDiscount,
      shoppingCartTotal: totalAmount,
      shoppingCartTotalTax: totalTax,
      isCouponApplied: false
    };
  }

  /**
   * @function fetchShoppingCartSubscriptionAndCodesDetails
   * @description Method for fetching shopping cart details for subscriptions.
   * @param {String} id Company Id.
   * @param {String} authToken Authentication Token.
   * @param {Array} companySubscriptionIds Subscription Ids.
   */
  async fetchShoppingCartSubscriptionAndCodesDetails(id, authToken, companySubscriptionIds, oneOffCodes) {
    let proxy = new GetPriceProxy();
    let chargedAmount = await proxy.getPrice({
      id,
      authToken,
      companySubscriptionIds,
      oneOffCodes,
    });
    let shoppingCart = [];
    let isCouponApplied = false;
    if (chargedAmount.items) {
      let fetchedShoppingCart = chargedAmount.items.reduce((map, obj) => {
        map[obj.code] = {
          description: obj.description,
          amount: obj.amount,
          code: obj.code,
          couponCode: obj.couponCode,
        };
        return map;
      }, {});
      isCouponApplied = fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.COUPON_DISCOUNT);
      shoppingCart = await this.translateShoppingCartItems(fetchedShoppingCart);
    }
    return {
      shoppingCart,
      shoppingCartTotal: chargedAmount.totalAmount,
      shoppingCartSubTotal: chargedAmount.subTotal ? chargedAmount.subTotal : chargedAmount.totalAmount,
      shoppingCartDiscount: chargedAmount.totalDiscount ? chargedAmount.totalDiscount : 0,
      isCouponApplied,
    };
  }

  async fetchUpgradeCorpsecSubscriptionDetails(addShareholderId) {
    let proxy = new GetPriceProxy();
    let authToken = getToken();
    let store = new LocalStoreManager();
    let companyId = await store.getCompanyId();

    if (companyId == null || companyId === "") {
      return;
    }
    let chargedAmount = await proxy.getUpgradePrice({
      companyId,
      authToken,
      addShareholderId,
    });
    let shoppingCart = [];
    let isCouponApplied = false;
    if (chargedAmount.items) {
      let fetchedShoppingCart = chargedAmount.items.reduce((map, obj) => {
        map[obj.code] = {
          description: obj.description,
          amount: obj.amount,
          code: obj.code,
          couponCode: obj.couponCode,
        };
        return map;
      }, {});
      shoppingCart = await this.translateShoppingCartItems(fetchedShoppingCart);
    }
    return {
      shoppingCart,
      shoppingCartTotal: chargedAmount.totalAmount,
      shoppingCartSubTotal: chargedAmount.subTotal ? chargedAmount.subTotal : 0,
      shoppingCartDiscount: chargedAmount.totalDiscount ? chargedAmount.totalDiscount : 0,
      isCouponApplied,
    };
  }

  /**
   * @function translateShoppingCartItems
   * @description Method to display shopping cart items in UI.
   * @param {Object} fetchedShoppingCart Fetched shopping cart details.
   */
  // eslint-disable-next-line max-lines-per-function
  translateShoppingCartItems(fetchedShoppingCart) {
    let shoppingCart = [];

    forEach(fetchedShoppingCart, function (value) {
      shoppingCart.push({
        description: value.description,
        amount: value.amount,
        code: value.code,
        couponCode: value.couponCode,
        isIncludedOnAccountingPlan: value.isIncludedOnAccountingPlan,
      });
    });

    // DEPRECATED we should use the xero item descriptions and amount exactly what's in xero
    // if (fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.CORPORATE_SECRETARY)) {
    //   shoppingCart.push({
    //     description: "Corporate Secretary",
    //     amount: fetchedShoppingCart[SHOPPING_CART_ITEMS.CORPORATE_SECRETARY].amount
    //   });
    // }
    // if (fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.REGISTERED_ADDRESS)) {
    //   shoppingCart.push({
    //     description: "Registered Address",
    //     amount: fetchedShoppingCart[SHOPPING_CART_ITEMS.REGISTERED_ADDRESS].amount
    //   });
    // }
    // if (
    //   fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.INCORPORATION) &&
    //   fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.INCORPORATION_DISCOUNT)
    // ) {
    //   shoppingCart.push({
    //     description: "Company Incorporation",
    //     amount:
    //       fetchedShoppingCart[SHOPPING_CART_ITEMS.INCORPORATION].amount +
    //       fetchedShoppingCart[SHOPPING_CART_ITEMS.INCORPORATION_DISCOUNT].amount
    //   });
    // } else if (fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.INCORPORATION)) {
    //   shoppingCart.push({
    //     description: "Incorporation",
    //     amount: fetchedShoppingCart[SHOPPING_CART_ITEMS.INCORPORATION].amount
    //   });
    // }
    // if (fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.NOMINEE_DIRECTOR_THREE_MONTHS)) {
    //   shoppingCart.push({
    //     description: "Nominee Director",
    //     amount: fetchedShoppingCart[SHOPPING_CART_ITEMS.NOMINEE_DIRECTOR_THREE_MONTHS].amount
    //   });
    // }
    // if (fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.NOMINEE_DIRECTOR_SIX_MONTHS)) {
    //   shoppingCart.push({
    //     description: "Nominee Director",
    //     amount: fetchedShoppingCart[SHOPPING_CART_ITEMS.NOMINEE_DIRECTOR_SIX_MONTHS].amount
    //   });
    // }
    // if (fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.NOMINEE_DIRECTOR_ONE_YEAR)) {
    //   shoppingCart.push({
    //     description: "Nominee Director",
    //     amount: fetchedShoppingCart[SHOPPING_CART_ITEMS.NOMINEE_DIRECTOR_ONE_YEAR].amount
    //   });
    // }
    // if (fetchedShoppingCart.hasOwnProperty(SHOPPING_CART_ITEMS.COUPON_DISCOUNT)) {
    //   shoppingCart.push({
    //     description: fetchedShoppingCart[SHOPPING_CART_ITEMS.COUPON_DISCOUNT].description,
    //     amount: fetchedShoppingCart[SHOPPING_CART_ITEMS.COUPON_DISCOUNT].amount
    //   });
    // }
    return shoppingCart;
  }

  handleMicroserviceShoppingCartDetails(
    company,
    companyId,
    authToken,
    couponEnabled,
    subscriptionIds = [],
    previewDiscount = false,
    appFeatures = null,
    couponCode = null
  ) {
    if (
      company.status == COMPANY_STATUSES.DRAFT.value ||
      company.status == PARTNER_COMPANY_STATUSES.PARTNER_DRAFT.value
    ) {
      return this.fetchOnboardingShoppingCartDetailsMicroservice(companyId, authToken, previewDiscount, couponCode);
    } else {
      return this.fetchShoppingCartSubscriptionsDetailsMicroservice(
        authToken,
        subscriptionIds,
        companyId,
        appFeatures,
        couponCode
      );
    }
  }

  /**
   * @function publishChange
   * @description Method to publish message globally.
   * @param {Object} msg message to be published.
   */
  static publishChange(msg) {
    globalTopic.publish(msg);
  }

  /**
   * @function addListener
   * @description Method to publish message globally.
   * @param {Function} strategy subscriber strategy.
   */
  static addListener(strategy) {
    globalTopic.subscribe(strategy);
  }

  static clearListener() {
    globalTopic.unsubscribe();
  }
}
